import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, ApiError, type ActivityResponse, type ActivitySessionResponse, type ExerciseRow } from "../lib/api";
import { fmtDuration, fmtKcal, fmtKm, fmtNumber, fmtPace, fmtTimeShort } from "../lib/format";
import { PageHeader, EmptyState } from "../components/EmptyState";

const TYPES = ["walk", "run", "cycle", "strength", "yoga", "swim", "other"];
const ACCENT = "#9a3412";
const FAINT = "#d6b8a6";

/** Sessions of one type this close together are one outing the watch split up. */
const JOIN_GAP_MIN = 20;

interface Workout {
  key: string;
  type: string;
  started_at: string;
  pieces: ExerciseRow[];
  duration_min: number;
  distance_km: number | null;
  calories: number | null;
  steps: number | null;
  avg_hr: number | null;
  max_hr: number | null;
  elevation_m: number | null;
  max_speed_mps: number | null;
}

function endMs(s: ExerciseRow) {
  return s.ended_at ? new Date(s.ended_at).getTime() : new Date(s.started_at).getTime() + s.duration_min * 6e4;
}

/** Joins back-to-back sessions of the same type, newest first. Stored rows are untouched. */
function toWorkouts(sessions: ExerciseRow[]): Workout[] {
  const asc = [...sessions].sort((a, b) => a.started_at.localeCompare(b.started_at));
  const groups: ExerciseRow[][] = [];
  for (const s of asc) {
    const last = groups[groups.length - 1];
    const prev = last?.[last.length - 1];
    if (prev && prev.activity_type === s.activity_type && new Date(s.started_at).getTime() - endMs(prev) <= JOIN_GAP_MIN * 6e4) {
      last.push(s);
    } else {
      groups.push([s]);
    }
  }
  const sum = (xs: (number | null | undefined)[]) => {
    const v = xs.filter((x): x is number => x != null);
    return v.length ? v.reduce((a, b) => a + b, 0) : null;
  };
  return groups
    .map((p) => {
      const duration = p.reduce((a, s) => a + s.duration_min, 0);
      const withHr = p.filter((s) => s.avg_hr != null);
      const hrMin = withHr.reduce((a, s) => a + s.duration_min, 0);
      return {
        key: p.map((s) => s.id).join("-"),
        type: p[0].activity_type,
        started_at: p[0].started_at,
        pieces: p,
        duration_min: duration,
        distance_km: sum(p.map((s) => s.distance_km)),
        calories: sum(p.map((s) => s.calories_burned)),
        steps: sum(p.map((s) => s.steps)),
        avg_hr: hrMin > 0 ? Math.round(withHr.reduce((a, s) => a + s.avg_hr! * s.duration_min, 0) / hrMin) : null,
        max_hr: p.reduce<number | null>((m, s) => (s.max_hr != null && (m == null || s.max_hr > m) ? s.max_hr : m), null),
        elevation_m: sum(p.map((s) => s.elevation_gain_m)),
        max_speed_mps: p.reduce<number | null>((m, s) => (s.max_speed_mps != null && (m == null || s.max_speed_mps > m) ? s.max_speed_mps : m), null),
      };
    })
    .reverse();
}

/** Local Monday of the week a moment falls in. */
function weekOf(iso: string) {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

export default function Activity() {
  const nav = useNavigate();
  const [type, setType] = useState("run");
  const [duration, setDuration] = useState("");
  const [calories, setCalories] = useState("");
  const [distance, setDistance] = useState("");
  const [steps, setSteps] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [activity, setActivity] = useState<ActivityResponse | null | "error">(null);
  const [filter, setFilter] = useState<"all" | "run" | "walk">("all");
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api
      .activity(14, 200)
      .then(setActivity)
      .catch(() => setActivity("error"));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const dur = Number(duration);
    if (!Number.isFinite(dur) || dur <= 0) {
      setErr("Duration required");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.postExercise({
        activity_type: type,
        duration_min: dur,
        calories_burned: calories ? Number(calories) : undefined,
        distance_km: distance ? Number(distance) : undefined,
        steps: steps ? Number(steps) : undefined,
      });
      nav("/");
    } catch (e) {
      setErr(e instanceof ApiError ? `API ${e.status}` : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const today = activity && activity !== "error" ? activity.today : null;
  const workouts = useMemo(
    () => (activity && activity !== "error" ? toWorkouts(activity.sessions) : []),
    [activity]
  );
  const shown = workouts.filter((w) => filter === "all" || w.type === filter);

  const weeks = useMemo(() => {
    const map = new Map<number, Workout[]>();
    for (const w of shown) {
      const k = weekOf(w.started_at).getTime();
      map.set(k, [...(map.get(k) ?? []), w]);
    }
    return [...map.entries()].sort((a, b) => b[0] - a[0]);
  }, [shown]);

  const last7 = workouts.filter((w) => Date.now() - new Date(w.started_at).getTime() < 7 * 864e5);
  const runs7 = last7.filter((w) => w.type === "run");

  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      <PageHeader title="Activity" eyebrow="Steps · workouts · heart rate" />

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Steps today" value={today?.steps != null ? fmtNumber(today.steps, 0) : "—"} />
        <Stat label="Active today" value={today?.active_minutes != null ? `${today.active_minutes}m` : "—"} />
        <Stat label="Runs · 7 days" value={`${runs7.length} · ${fmtKm(runs7.reduce((a, w) => a + (w.distance_km ?? 0), 0))}`} />
        <Stat label="Rest HR" value={today?.resting_hr != null ? `${today.resting_hr}` : "—"} />
      </div>

      <div className="mt-8 mb-3 flex items-center justify-between">
        <h2 className="section-title">Workouts</h2>
        <div className="flex gap-1">
          {(["all", "run", "walk"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize transition duration-fast ${
                filter === f ? "bg-accent/10 text-accent" : "text-ink-muted hover:text-ink"
              }`}
            >
              {f === "all" ? "All" : `${f}s`}
            </button>
          ))}
        </div>
      </div>

      {activity === null ? (
        <div className="text-ink-subtle">Loading…</div>
      ) : shown.length === 0 ? (
        <EmptyState error={activity === "error"} title="No workouts yet">
          Workouts recorded on the watch arrive through the phone bridge. You can also log one
          manually below.
        </EmptyState>
      ) : (
        <div className="space-y-6">
          {weeks.map(([weekStart, ws]) => {
            const km = ws.reduce((a, w) => a + (w.distance_km ?? 0), 0);
            const min = ws.reduce((a, w) => a + w.duration_min, 0);
            return (
              <section key={weekStart}>
                <div className="mb-2 flex items-baseline justify-between text-xs text-ink-subtle">
                  <span className="eyebrow">
                    Week of {new Date(weekStart).toLocaleDateString([], { day: "numeric", month: "short" })}
                  </span>
                  <span className="tabular-nums">
                    {ws.length} {ws.length === 1 ? "workout" : "workouts"} · {fmtKm(km)} · {fmtDuration(min)}
                  </span>
                </div>
                <div className="card divide-y divide-ink-line-soft overflow-hidden">
                  {ws.map((w) => (
                    <WorkoutRow key={w.key} w={w} open={open === w.key} onToggle={() => setOpen(open === w.key ? null : w.key)} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <details className="card-pad mt-8">
        <summary className="field-label cursor-pointer">Log a workout manually</summary>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <Field label="Activity">
            <select value={type} onChange={(e) => setType(e.target.value)} className="input">
              {TYPES.map((t) => (
                <option key={t} value={t} className="capitalize">
                  {t}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Duration (min)">
            <input inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value)} className="input" placeholder="45" />
          </Field>

          <div className="grid grid-cols-3 gap-2">
            <Field label="Cal burned">
              <input inputMode="numeric" value={calories} onChange={(e) => setCalories(e.target.value)} className="input" />
            </Field>
            <Field label="Distance km">
              <input inputMode="decimal" value={distance} onChange={(e) => setDistance(e.target.value)} className="input" />
            </Field>
            <Field label="Steps">
              <input inputMode="numeric" value={steps} onChange={(e) => setSteps(e.target.value)} className="input" />
            </Field>
          </div>

          {err && <div className="text-sm text-loss">{err}</div>}
          <button disabled={busy} className="btn-accent w-full py-3 text-base">
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </details>
    </div>
  );
}

function WorkoutRow({ w, open, onToggle }: { w: Workout; open: boolean; onToggle: () => void }) {
  const moves = w.distance_km != null && w.distance_km > 0;
  return (
    <div>
      <button type="button" onClick={onToggle} className="w-full px-4 py-3 text-left transition duration-fast hover:bg-ink-line-soft/40">
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <span className="font-medium capitalize text-ink">{w.type}</span>
            <span className="ml-2 text-xs text-ink-subtle">{fmtTimeShort(w.started_at)}</span>
            {w.pieces.length > 1 && <span className="pill-neutral ml-2">{w.pieces.length} parts</span>}
          </div>
          <div className="shrink-0 font-display text-lg tabular-nums text-ink">{moves ? fmtKm(w.distance_km) : fmtDuration(w.duration_min)}</div>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs tabular-nums text-ink-muted">
          <span>{fmtDuration(w.duration_min)}</span>
          {moves && <span>{fmtPace({ km: w.distance_km, min: w.duration_min })}</span>}
          <span>{w.avg_hr != null ? `♥ ${w.avg_hr} avg · ${w.max_hr ?? "—"} max` : "♥ no HR recorded"}</span>
          {w.calories != null && <span>{fmtKcal(w.calories)}</span>}
        </div>
      </button>
      {open && <WorkoutDetail w={w} />}
    </div>
  );
}

function WorkoutDetail({ w }: { w: Workout }) {
  const [hr, setHr] = useState<ActivitySessionResponse["heart_rate"] | null | "error">(null);

  useEffect(() => {
    Promise.all(w.pieces.map((p) => api.activitySession(p.id)))
      .then((rs) => {
        const seen = new Map<string, ActivitySessionResponse["heart_rate"][number]>();
        for (const r of rs) for (const b of r.heart_rate) seen.set(b.time, b);
        setHr([...seen.values()].sort((a, b) => a.time.localeCompare(b.time)));
      })
      .catch(() => setHr("error"));
  }, [w]);

  const points = hr && hr !== "error"
    ? hr.map((b) => ({
        t: new Date(b.time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
        avg: b.avg_bpm,
        max: b.max_bpm,
      }))
    : [];

  return (
    <div className="border-t border-ink-line-soft bg-canvas px-4 pb-4 pt-3">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Steps" value={w.steps != null ? fmtNumber(w.steps, 0) : "—"} />
        <Stat label="Best speed" value={w.max_speed_mps != null ? fmtPace({ mps: w.max_speed_mps }) : "—"} />
        <Stat label="Climb" value={w.elevation_m != null ? `${Math.round(w.elevation_m)} m` : "—"} />
      </div>

      <div className="mt-4 field-label">Heart rate</div>
      {hr === null ? (
        <div className="mt-2 text-xs text-ink-subtle">Loading…</div>
      ) : hr === "error" ? (
        <div className="mt-2 text-xs text-loss">Couldn't load heart rate.</div>
      ) : points.length < 2 ? (
        <div className="mt-2 text-xs text-ink-subtle">
          The watch recorded no heart rate during this workout. Continuous measurement on the watch fixes this.
        </div>
      ) : (
        <div className="mt-2 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
              <XAxis dataKey="t" axisLine={false} tickLine={false} fontSize={11} minTickGap={24} />
              <YAxis axisLine={false} tickLine={false} fontSize={11} domain={["dataMin - 10", "dataMax + 5"]} />
              <Tooltip formatter={(v: number, name: string) => [`${v} bpm`, name === "avg" ? "Average" : "Peak"]} />
              <Line type="monotone" dataKey="max" stroke={FAINT} dot={false} strokeWidth={1.5} connectNulls />
              <Line type="monotone" dataKey="avg" stroke={ACCENT} dot={false} strokeWidth={2} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {w.pieces.length > 1 && (
        <div className="mt-3 text-xs text-ink-subtle">
          Recorded as {w.pieces.length} back-to-back sessions:{" "}
          {w.pieces.map((p) => `${fmtKm(p.distance_km)} in ${fmtDuration(p.duration_min)}`).join(", ")}.
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-ink-line bg-canvas px-2 py-2 text-center">
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="mt-0.5 font-display text-lg tabular-nums text-ink">{value}</div>
    </div>
  );
}
